import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Mic, MicOff, Loader2, Sparkles, Volume2, Square, Stethoscope, User, Check, Pause, Play, Settings, Globe, FileText, Coins, BookOpen, MessageCircle, Phone, PhoneOff, QrCode, Copy, ArrowLeftRight, Info } from 'lucide-react';
import QRCode from 'qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useAzureSpeechRecording, TranscriptMessage } from '@/hooks/useAzureSpeechRecording';
import { useSip, SipConfig } from '@/hooks/useSip';
import { mixAudioStreams } from '@/lib/audio-mixer';
import { api, metHerhaalAdvies } from '@/lib/api';
import { tekstNaarSpraak, transcriptNaarSoep, vertaal } from '@/lib/ai';
import { toast } from '@/hooks/use-toast';
import { AZURE_SPEECH_LANGUAGES } from '@/lib/azure-languages';
import { VOICE_TRANSLATE_COMMAND_ENABLED } from '@/lib/feature-flags';
import { PracticeSettings, DEFAULT_PRACTICE_SETTINGS } from '@/types/practice-settings';
import { useConsultationCosts } from '@/hooks/useConsultationCosts';
import { VerlatenWaarschuwing } from '@/components/VerlatenWaarschuwing';
import type { LiveTranscriptAntwoord } from '@/lib/api-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Speaker color palette
const DOCTOR_BG = 'bg-primary/10 dark:bg-primary/20';
const PATIENT_BG = 'bg-muted/40 dark:bg-muted/30';
const UNKNOWN_BG = 'bg-muted/20';

// Azure TTS voice mapping per language code
const AZURE_TTS_VOICES: Record<string, string> = {
  'nl-NL': 'nl-NL-MaartenNeural',
  'en-US': 'en-US-GuyNeural',
  'en-GB': 'en-GB-RyanNeural',
  'de-DE': 'de-DE-ConradNeural',
  'fr-FR': 'fr-FR-HenriNeural',
  'fr-BE': 'fr-BE-GerardNeural',
  'es-ES': 'es-ES-AlvaroNeural',
  'pt-PT': 'pt-PT-DuarteNeural',
  'pt-BR': 'pt-BR-AntonioNeural',
  'it-IT': 'it-IT-DiegoNeural',
  'pl-PL': 'pl-PL-MarekNeural',
  'tr-TR': 'tr-TR-AhmetNeural',
  'ar-SA': 'ar-SA-HamedNeural',
  'ar-MA': 'ar-MA-JamalNeural',
  'zh-CN': 'zh-CN-YunxiNeural',
  'ja-JP': 'ja-JP-KeitaNeural',
  'ko-KR': 'ko-KR-InJoonNeural',
  'ru-RU': 'ru-RU-DmitryNeural',
  'uk-UA': 'uk-UA-OstapNeural',
  'ro-RO': 'ro-RO-EmilNeural',
  'bg-BG': 'bg-BG-BorislavNeural',
  'cs-CZ': 'cs-CZ-AntoninNeural',
  'da-DK': 'da-DK-JeppeNeural',
  'fi-FI': 'fi-FI-HarriNeural',
  'el-GR': 'el-GR-NestorasNeural',
  'hi-IN': 'hi-IN-MadhurNeural',
  'hu-HU': 'hu-HU-TamasNeural',
  'id-ID': 'id-ID-ArdiNeural',
  'ms-MY': 'ms-MY-OsmanNeural',
  'nb-NO': 'nb-NO-FinnNeural',
  'sv-SE': 'sv-SE-MattiasNeural',
  'th-TH': 'th-TH-NiwatNeural',
  'vi-VN': 'vi-VN-NamMinhNeural',
  'he-IL': 'he-IL-AvriNeural',
  'sr-RS': 'sr-RS-NicholasNeural',
  'hr-HR': 'hr-HR-SreckoNeural',
  'sk-SK': 'sk-SK-LukasNeural',
  'sl-SI': 'sl-SI-RokNeural',
  'ca-ES': 'ca-ES-EnricNeural',
  'eu-ES': 'eu-ES-AnderNeural',
  'gl-ES': 'gl-ES-RoiNeural',
  'cy-GB': 'cy-GB-AledNeural',
  'ga-IE': 'ga-IE-ColmNeural',
  'af-ZA': 'af-ZA-WillemNeural',
  'sw-KE': 'sw-KE-RafikiNeural',
  'bn-IN': 'bn-IN-BashkarNeural',
  'ta-IN': 'ta-IN-ValluvarNeural',
  'te-IN': 'te-IN-MohanNeural',
  'ur-PK': 'ur-PK-AsadNeural',
  'fa-IR': 'fa-IR-FaridNeural',
};

function getVoiceForLanguage(langCode?: string): string {
  if (!langCode) return AZURE_TTS_VOICES['nl-NL'];
  return AZURE_TTS_VOICES[langCode] || AZURE_TTS_VOICES['nl-NL'];
}

function getLanguageLabel(langCode: string): string {
  return AZURE_SPEECH_LANGUAGES.find(l => l.code === langCode)?.label || langCode;
}

/**
 * De kale taalnaam, zonder het land tussen haakjes: "Engels (VK)" wordt "Engels".
 * Alleen bedoeld voor middenin een zin -- in de keuzelijsten blijft het hele label staan.
 */
function getLanguageName(langCode: string): string {
  return getLanguageLabel(langCode).replace(/\s*\([^)]*\)\s*$/, '');
}

/**
 * De uitleg over het vertalen, met de taal van de zorgverlener erin. De vaste
 * vertalingen verderop gaan er allemaal van uit dat die Nederlands spreekt; spreekt
 * hij iets anders, dan laten we deze Nederlandse brontekst ter plekke vertalen.
 */
function bouwVertaalUitleg(providerLang: string): string {
  const taal = getLanguageName(providerLang);
  return `Welkom. De computer gaat ons helpen met vertalen tijdens dit gesprek. Wanneer de dokter iets in het ${taal} zegt, wordt dit automatisch voor u vertaald naar uw taal. En wanneer u in uw eigen taal spreekt, vertaalt de computer dit naar het ${taal} voor de dokter. U kunt dus gewoon in uw eigen taal spreken.`;
}

interface AudioRecordingPanelProps {
  onSoepGenerated: (soep: {
    subjective: string;
    objective: string;
    evaluation: string;
    plan: string;
  }, transcript: string, problems?: Array<{
    title: string;
    subjective: string;
    objective: string;
    evaluation: string;
    plan: string;
  }>,
  /** De id van deze generatie, waarop het beoordeelscherm zijn duim vastlegt. */
  generationId?: string | null) => void;
  onClose: () => void;
  languages?: string[];
  practiceSettings?: PracticeSettings;
  onOpenSettings?: () => void;
  onOpenManual?: () => void;
  practiceUserId?: string | null;
  /**
   * accessToken en appToken zijn hier weg. Die werden meegegeven omdat elke
   * edge-function-aanroep ze in zijn body verwachtte; de sessie zit nu in een
   * HttpOnly-cookie, dus dit component hoeft niet meer te weten dat er tokens
   * bestaan.
   */
  consultationCosts?: import('@/hooks/useConsultationCosts').ConsultationCostTracker;
}

type SpeakerRole = 'arts' | 'patient';

// Check if text is essentially just a command phrase (strip punctuation/whitespace)
function isOnlyPhrase(text: string, phrase: string): boolean {
  const cleaned = text.replace(/[.,!?;:…\s]+/g, ' ').trim().toLowerCase();
  return cleaned === phrase.toLowerCase();
}

export function AudioRecordingPanel({ onSoepGenerated, onClose, languages, practiceSettings, onOpenSettings, onOpenManual, practiceUserId, consultationCosts }: AudioRecordingPanelProps) {
  const settings = practiceSettings || DEFAULT_PRACTICE_SETTINGS;
  const [isProcessing, setIsProcessing] = useState(false);
  // Per-sessie override: één SOEP-verslag i.p.v. meerdere deelcontacten.
  const [singleSoep, setSingleSoep] = useState<boolean>(settings.force_single_soep ?? false);
  useEffect(() => { setSingleSoep(settings.force_single_soep ?? false); }, [settings.force_single_soep]);
  const multipleProblems = !singleSoep;
  const fallbackCosts = useConsultationCosts();
  const { costs, voegToe: voegKostenToe } = consultationCosts || fallbackCosts;

  // Zowel de taal van de zorgverlener als die van de patiënt is per consult instelbaar.
  const configuredLangs = languages && languages.length > 0 ? languages : ['nl-NL'];
  const [providerLang, setProviderLang] = useState<string>(
    configuredLangs.find(l => l.startsWith('nl')) || 'nl-NL'
  );
  const [patientLang, setPatientLang] = useState<string>(
    configuredLangs.find(l => l !== 'nl-NL') || configuredLangs[0] || 'nl-NL'
  );

  // Build effective languages array from the two dropdowns (unique)
  const effectiveLanguages = Array.from(new Set([providerLang, patientLang]));
  // Only translate when provider and patient speak different languages
  const shouldTranslate = settings.auto_translate !== false && providerLang !== patientLang;
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  // Map speaker label -> role
  const [speakerRoles, setSpeakerRoles] = useState<Record<string, SpeakerRole>>({});
  const speakerRolesRef = useRef<Record<string, SpeakerRole>>({});
  useEffect(() => { speakerRolesRef.current = speakerRoles; }, [speakerRoles]);
  const [doctorAutoAssigned, setDoctorAutoAssigned] = useState(false);
  const [readAloudIds, setReadAloudIds] = useState<Set<string>>(new Set());
  const [triggerCommandIds, setTriggerCommandIds] = useState<Set<string>>(new Set());
  const triggerCommandIdsRef = useRef<Set<string>>(new Set());
  const lastAutoPlayedCount = useRef(0);
  const isTranslateQueueRunningRef = useRef(false);
  const lastProcessedTriggerIdRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ttsQueueRef = useRef<TranscriptMessage[]>([]);
  const isPlayingQueueRef = useRef(false);
  const readAloudIdsRef = useRef<Set<string>>(new Set());
  const lastTranslatedQueueRef = useRef<TranscriptMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<TranscriptMessage[]>([]);
  const recordingStartTime = useRef<number | null>(null);

  // ===== SIP/WebRTC phone call (FreePBX via JsSIP) =====
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sipActivationKey, setSipActivationKey] = useState(0);
  const [callTranscriptionStream, setCallTranscriptionStream] = useState<MediaStream | null>(null);
  const mixedStreamRef = useRef<{ stream: MediaStream; stop: () => void } | null>(null);
  const [callDurationTick, setCallDurationTick] = useState(0);

  // ===== Live transcript (QR share) =====
  const [liveDialogOpen, setLiveDialogOpen] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveToken, setLiveToken] = useState<string | null>(null);
  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [liveQrDataUrl, setLiveQrDataUrl] = useState<string | null>(null);
  /**
   * Het live-transcripttoken, zolang deze opname loopt.
   *
   * Vervangt de Supabase-broadcastchannel. Publiceren is nu een POST naar
   * /api/live-transcript/<token>/messages; de telefoon van de patiënt leest een
   * server-sent-events-stream. Zie apps/api/src/lib/live-transcript.ts voor waarom
   * de berichten niet worden opgeslagen en wat de beperking is.
   */
  const liveTokenRef = useRef<string | null>(null);
  const liveBroadcastedRef = useRef<Map<string, string>>(new Map());
  /** Hoeveel telefoons meelezen. Was in de bron niet te zien. */
  const [liveMeelezers, setLiveMeelezers] = useState(0);

  const sipConfig = useMemo<SipConfig | null>(() => {
    if (!settings.calling_enabled) return null;
    const host = settings.sip_host?.trim();
    const ext = settings.sip_extension?.trim();
    const pw = settings.sip_password;
    if (!host || !ext || !pw) return null;
    const rawPort = settings.sip_ws_port?.trim() || '';
    const port = rawPort === '8089' ? '' : rawPort;
    const path = (settings.sip_ws_path?.trim() || '/ws').startsWith('/')
      ? (settings.sip_ws_path?.trim() || '/ws')
      : `/${settings.sip_ws_path?.trim() || 'ws'}`;
    return {
      ws_uri: `wss://${host}${port ? `:${port}` : ''}${path}`,
      sip_uri: `sip:${ext}@${host}`,
      sip_password: pw,
      display_name: settings.sip_display_name || null,
      stun_server: settings.sip_stun_server || null,
      dial_prefix: settings.sip_dial_prefix || null,
    };
  }, [settings.calling_enabled, settings.sip_host, settings.sip_extension, settings.sip_password, settings.sip_ws_port, settings.sip_ws_path, settings.sip_display_name, settings.sip_stun_server, settings.sip_dial_prefix]);

  const sip = useSip(sipConfig, sipActivationKey);
  useEffect(() => {
    if (sip.error) {
      toast({ title: 'Bellen mislukt', description: sip.error, variant: 'destructive' });
    }
  }, [sip.error]);

  // Automatisch verbinden met SIP zodra de configuratie beschikbaar is
  useEffect(() => {
    if (sipConfig && sipActivationKey === 0) {
      setSipActivationKey(1);
    }
  }, [sipConfig, sipActivationKey]);

  /** Eén regel transcript naar de telefoon van de patiënt. */
  const publiceerLiveBericht = useCallback(
    async (
      token: string,
      bericht: {
        id: string;
        speaker: string;
        role: 'arts' | 'patient' | null;
        text: string;
        translatedText: string | null;
        timestamp: string;
      },
    ) => {
      try {
        const antwoord = await api.post<{ ok: boolean; listeners: number }>(
          `/live-transcript/${encodeURIComponent(token)}/messages`,
          bericht,
        );
        setLiveMeelezers(antwoord.listeners);
      } catch (fout) {
        // Het transcript loopt door als de patiënt niet meeleest; dat is geen reden
        // om het consult te onderbreken.
        console.error('Live bericht versturen mislukt', fout);
      }
    },
    [],
  );

  // De QR-dialoog openen: sessie aanmaken, QR tekenen, en wat al gezegd is nasturen
  const handleOpenLiveTranscript = useCallback(async () => {
    setLiveDialogOpen(true);
    if (liveToken) return; // session already created for this recording
    setLiveLoading(true);
    setLiveError(null);
    try {
      const antwoord = await api.post<LiveTranscriptAntwoord>('/live-transcript');
      const tok = antwoord.token;
      const shareUrl = `${window.location.origin}/live/${tok}`;
      setLiveToken(tok);
      setLiveUrl(shareUrl);
      liveTokenRef.current = tok;
      const qr = await QRCode.toDataURL(shareUrl, { width: 320, margin: 1, errorCorrectionLevel: 'M' });
      setLiveQrDataUrl(qr);

      // Wat er al gezegd is, alsnog versturen. In de bron gebeurde dit in de
      // SUBSCRIBED-callback van het kanaal; hier is er niets om op te wachten, want
      // de server bewaart geen berichten en de telefoon abonneert zichzelf.
      for (const m of messagesRef.current) {
        if (m.isPartial || triggerCommandIdsRef.current.has(m.id)) continue;
        liveBroadcastedRef.current.set(m.id, `${m.text}||${m.translatedText || ''}`);
        void publiceerLiveBericht(tok, {
          id: m.id,
          speaker: m.speaker || '',
          role: speakerRolesRef.current[m.speaker] || null,
          text: m.text,
          translatedText: m.translatedText || null,
          timestamp: (m.timestamp instanceof Date ? m.timestamp : new Date()).toISOString(),
        });
      }
    } catch (e) {
      setLiveError(e instanceof Error ? e.message : 'Kon QR-sessie niet aanmaken.');
    } finally {
      setLiveLoading(false);
    }
  }, [liveToken, publiceerLiveBericht]);

  /**
   * De opnameduur melden.
   *
   * Veel smaller dan log-usage. Die functie nam een willekeurig usage_type en een
   * vrij metadata-object aan, en dit component gebruikte dat ook zo: het logde
   * 'translation' met een input_cost die het zelf uitrekende. De client bepaalde dus
   * wat er aan een praktijk werd doorbelast. Nu is er één ding dat de browser mag
   * melden -- hoe lang er is opgenomen -- want dat kan de server niet zelf weten.
   *
   * De teller wordt bijgewerkt met het bedrag dat de server teruggeeft; de tarieven
   * en de marge blijven daar.
   */
  const meldOpnameduur = useCallback(
    async (seconds: number, language?: string) => {
      if (!practiceUserId || seconds <= 0) return;
      try {
        const antwoord = await api.post<{ ok: boolean; logged: boolean; cost: number }>('/usage', {
          seconds,
          usageType: 'speech_recording',
          ...(language ? { language } : {}),
        });
        voegKostenToe('recording', antwoord.cost);
      } catch (fout) {
        // Een mislukte verbruiksmelding mag een consult niet in de weg staan.
        console.error('Verbruik melden mislukt', fout);
      }
    },
    [practiceUserId, voegKostenToe],
  );

  
  const {
    isRecording,
    isPaused,
    isConnecting,
    isTranslating,
    fullTranscript,
    messages,
    partialMessage,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    updateMessage,
  } = useAzureSpeechRecording({
    languages: effectiveLanguages,
    audioStream: callTranscriptionStream,
    onError: (error) => {
      toast({
        title: "Opnamefout",
        description: error,
        variant: "destructive",
      });
    },
    isCommandText: (text) => {
      if (!VOICE_TRANSLATE_COMMAND_ENABLED) return false;
      const triggerPhrase = settings.read_trigger_phrase?.trim().toLowerCase();
      if (triggerPhrase && isOnlyPhrase(text, triggerPhrase)) return true;
      return false;
    },
    skipTranslation: true, // Never translate in real-time; translation happens only when trigger command is spoken
    // De hook geeft door wat de server als kosten teruggaf; er wordt hier niets
    // meer uitgerekend.
    onTranslationCost: (kosten: number) => voegKostenToe('translation', kosten),
  });

  /**
   * Een lopende opname, en ook een opname die al gestopt is maar waarvan het
   * transcript er nog staat: in beide gevallen gooit het verlaten van dit scherm een
   * consult weg. Het transcript telt dus mee en niet alleen isRecording -- na een
   * mislukte SOEP-generatie staat het scherm stil met alle tekst er nog in, en juist
   * dan is dat het enige exemplaar.
   */
  const heeftOnbewaardConsult = isRecording || messages.length > 0;

  // Manual start recording handler
  const handleStartRecording = async () => {
    const success = await startRecording();
    if (success) {
      recordingStartTime.current = Date.now();
    } else {
      toast({
        title: "Opname niet gestart",
        description: "Probeer opnieuw of controleer je microfoon instellingen.",
        variant: "destructive",
      });
    }
  };

  // Build a mixed (local mic + remote SIP audio) stream as soon as the call is connected,
  // and start transcription on it. Stop transcription when the call ends.
  useEffect(() => {
    if (sip.isConnected && sip.call?.localStream && sip.call?.remoteStream && !mixedStreamRef.current) {
      const mixed = mixAudioStreams([sip.call.localStream, sip.call.remoteStream]);
      mixedStreamRef.current = mixed;
      setCallTranscriptionStream(mixed.stream);
    }
  }, [sip.isConnected, sip.call?.localStream, sip.call?.remoteStream]);

  // Auto-start transcription once the mixed stream is available
  useEffect(() => {
    if (callTranscriptionStream && !isRecording && !isConnecting) {
      startRecording().then(success => {
        if (success) recordingStartTime.current = Date.now();
      });
    }
  }, [callTranscriptionStream, isRecording, isConnecting, startRecording]);

  // Cleanup when call ends
  useEffect(() => {
    if (!sip.isInCall && mixedStreamRef.current) {
      mixedStreamRef.current.stop();
      mixedStreamRef.current = null;
      setCallTranscriptionStream(null);
      if (isRecording) stopRecording();
    }
  }, [sip.isInCall, isRecording, stopRecording]);

  // Tick every second while in call to update the duration label
  useEffect(() => {
    if (!sip.isConnected) return;
    const id = setInterval(() => setCallDurationTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [sip.isConnected]);

  const formatDuration = (ms: number) => {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60).toString().padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleCallToggle = async () => {
    if (sip.isInCall) {
      sip.hangup();
      return;
    }
    if (!sipConfig) {
      toast({ title: 'SIP niet geconfigureerd', description: 'Stel eerst de SIP-instellingen in.', variant: 'destructive' });
      return;
    }
    if (sip.state === 'idle' || sip.state === 'register-failed' || sip.state === 'ended') {
      setSipActivationKey((key) => key + 1);
      return;
    }
    if (sip.state !== 'registered') {
      toast({ title: 'SIP niet verbonden', description: `Status: ${sip.state}`, variant: 'destructive' });
      return;
    }
    let trimmed = phoneNumber.trim();
    if (!trimmed) {
      toast({ title: 'Geen telefoonnummer', description: 'Voer eerst een nummer of extensie in.', variant: 'destructive' });
      return;
    }
    if (trimmed.startsWith('0')) {
      trimmed = '+31' + trimmed.slice(1);
      setPhoneNumber(trimmed);
    }
    if (!trimmed.startsWith('+31')) {
      toast({
        title: 'Ongeldig telefoonnummer',
        description: 'Het telefoonnummer moet beginnen met +31 (Nederlands landnummer).',
        variant: 'destructive',
      });
      return;
    }
    await sip.dial(trimmed);
  };

  // Keep messagesRef in sync
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Elke afgeronde regel naar de telefoon van de patiënt.
  useEffect(() => {
    const tok = liveTokenRef.current;
    if (!tok) return;

    for (const m of messages) {
      if (m.isPartial) continue;
      if (triggerCommandIds.has(m.id)) continue;
      const sig = `${m.text}||${m.translatedText || ''}`;
      if (liveBroadcastedRef.current.get(m.id) === sig) continue;
      liveBroadcastedRef.current.set(m.id, sig);
      void publiceerLiveBericht(tok, {
        id: m.id,
        speaker: m.speaker || '',
        role: speakerRoles[m.speaker] || null,
        text: m.text,
        translatedText: m.translatedText || null,
        timestamp: (m.timestamp instanceof Date ? m.timestamp : new Date()).toISOString(),
      });
    }
  }, [messages, triggerCommandIds, speakerRoles, publiceerLiveBericht]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
        }
      });
    }
  }, [messages, partialMessage]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const assignSpeakerRole = useCallback((speakerLabel: string, role: SpeakerRole) => {
    setSpeakerRoles(prev => {
      const next: Record<string, SpeakerRole> = { ...prev };
      const allSpeakers = new Set(messages.map(m => m.speaker).filter(Boolean));
      const other: SpeakerRole = role === 'arts' ? 'patient' : 'arts';
      allSpeakers.forEach(s => {
        next[s] = s === speakerLabel ? role : other;
      });
      next[speakerLabel] = role;
      return next;
    });
  }, [messages]);

  const assignDoctorRole = useCallback((speakerLabel: string) => {
    assignSpeakerRole(speakerLabel, 'arts');
  }, [assignSpeakerRole]);


  // Auto-detect roles by detected language + smart mapping of new speakers
  useEffect(() => {
    if (messages.length === 0) return;
    const providerBase = providerLang.split('-')[0];
    const patientBase = patientLang.split('-')[0];
    const langDistinct = providerBase !== patientBase;

    setSpeakerRoles(prev => {
      const next = { ...prev };
      let changed = false;
      let lastRole: SpeakerRole | null = null;

      messages.forEach(m => {
        const spk = m.speaker;
        if (!spk) return;
        if (!next[spk]) {
          const base = m.detectedLanguage ? m.detectedLanguage.split('-')[0] : null;
          let inferred: SpeakerRole | null = null;
          if (langDistinct && base) {
            if (base === providerBase) inferred = 'arts';
            else if (base === patientBase) inferred = 'patient';
          }
          if (!inferred && lastRole) {
            // Turn-taking fallback: a new speaker is most likely the other party
            inferred = lastRole === 'arts' ? 'patient' : 'arts';
          }
          if (inferred) {
            next[spk] = inferred;
            changed = true;
          }
        }
        lastRole = next[spk] || lastRole;
      });

      return changed ? next : prev;
    });
  }, [messages, providerLang, patientLang]);

  const swapRoles = useCallback(() => {
    setSpeakerRoles(prev => {
      const next: Record<string, SpeakerRole> = {};
      Object.entries(prev).forEach(([spk, role]) => {
        next[spk] = role === 'arts' ? 'patient' : 'arts';
      });
      return next;
    });
  }, []);

  // Auto-detect doctor by recognition phrase
  useEffect(() => {
    if (doctorAutoAssigned) return;
    const rawPhrase = settings.doctor_phrase?.trim().toLowerCase();
    if (!rawPhrase) return;
    const phrases = rawPhrase.split(',').map(p => p.trim()).filter(Boolean);
    if (phrases.length === 0) return;
    
    for (const msg of messages) {
      const text = (msg.translatedText || msg.text).toLowerCase();
      if (phrases.some(phrase => text.includes(phrase))) {
        assignDoctorRole(msg.speaker);
        setDoctorAutoAssigned(true);
        break;
      }
    }
  }, [messages, doctorAutoAssigned, assignDoctorRole]);

  const getSpeakerDisplayName = (speaker: string): string => {
    const role = speakerRoles[speaker];
    if (role === 'arts') return '🩺 Zorgverlener';
    if (role === 'patient') return '🧑 Patiënt';
    return speaker;
  };

  const getSpeakerRole = (speaker: string): SpeakerRole | null => {
    return speakerRoles[speaker] || null;
  };

  const hasAnyRoleAssigned = Object.keys(speakerRoles).length > 0;

  const playTTSForMessage = useCallback(async (msg: TranscriptMessage, options?: { forceProviderLang?: boolean; targetLangOverride?: string; skipAutoTranslate?: boolean }) => {
    const forceProviderLang = options?.forceProviderLang ?? false;
    // Determine TTS language: doctor messages → patient language, patient messages → provider language
    const targetLang = options?.targetLangOverride || (forceProviderLang ? providerLang : patientLang);
    const needsTranslation = !forceProviderLang
      ? !patientLang.startsWith(providerLang.split('-')[0])   // doctor → patient: translate if languages differ
      : !providerLang.startsWith(patientLang.split('-')[0]);  // patient → provider: translate if languages differ
    const voice = getVoiceForLanguage(targetLang);
    
    // If translatedText already differs from text, it was pre-translated by the trigger — use it directly
    const alreadyTranslated = msg.translatedText && msg.translatedText !== msg.text;
    const sourceText = alreadyTranslated ? msg.translatedText : (msg.translatedText || msg.text);
    if (!sourceText.trim()) return;

    try {
      let textToSpeak = sourceText;

      // Only translate if not already pre-translated and languages differ
      if (needsTranslation && !alreadyTranslated && !options?.skipAutoTranslate) {
        const langLabel = getLanguageLabel(targetLang);
        try {
          // Geen aparte verbruiksmelding meer: de server logt de vertaling op het
          // moment dat hij hem maakt, en geeft terug wat hij kost.
          const { vertaling, kosten } = await vertaal(sourceText, langLabel, 'soep');
          if (vertaling) {
            textToSpeak = vertaling;
            voegKostenToe('translation', kosten);
          }
        } catch (e) {
          console.error('Vertalen voor het voorlezen mislukt:', e);
        }
      }

      const { audio: audioBlob, kosten: ttsKosten } = await tekstNaarSpraak(textToSpeak, voice);
      voegKostenToe('recording', ttsKosten);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      setPlayingMessageId(msg.id);

      await new Promise<void>((resolve) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.play().catch(() => resolve());
      });
    } catch (error) {
      console.error('TTS playback failed:', error);
    } finally {
      setPlayingMessageId(null);
      audioRef.current = null;
    }
  }, [providerLang, patientLang, voegKostenToe]);

  const processQueue = useCallback(async () => {
    if (isPlayingQueueRef.current) return;
    isPlayingQueueRef.current = true;
    while (ttsQueueRef.current.length > 0) {
      const queued = ttsQueueRef.current.shift()!;
      // Merge latest message state, but NEVER lose a translation already attached to the queued copy.
      const latest = messagesRef.current.find(m => m.id === queued.id);
      const next = latest
        ? {
            ...latest,
            translatedText: queued.translatedText || latest.translatedText,
          }
        : queued;
      const role = speakerRolesRef.current[next.speaker];
      const isDoctor = role === 'arts';
      const ttsLang = isDoctor ? patientLang : providerLang;
      await playTTSForMessage(next, { targetLangOverride: ttsLang, skipAutoTranslate: true });
      readAloudIdsRef.current.add(next.id);
      setReadAloudIds(prev => new Set(prev).add(next.id));
    }
    isPlayingQueueRef.current = false;
  }, [playTTSForMessage, providerLang]);

  // Uitleg over het vertalen, in de taal van de patiënt. Deze vaste vertalingen
  // noemen de zorgverlener Nederlandssprekend; spreekt hij een andere taal, dan
  // gebruikt handlePlayIntro bouwVertaalUitleg() in plaats van deze tabel.
  const INTRO_TEXTS: Record<string, string> = {
    'nl-NL': 'Welkom. De computer gaat ons helpen met vertalen tijdens dit gesprek. Wanneer de dokter iets in het Nederlands zegt, wordt dit automatisch voor u vertaald naar uw taal. En wanneer u in uw eigen taal spreekt, vertaalt de computer dit naar het Nederlands voor de dokter. U kunt dus gewoon in uw eigen taal spreken.',
    'en-US': 'Welcome. The computer will help us with translation during this conversation. When the doctor speaks in Dutch, it will be automatically translated into your language for you. And when you speak in your own language, the computer will translate it into Dutch for the doctor. So you can simply speak in your own language.',
    'en-GB': 'Welcome. The computer will help us with translation during this conversation. When the doctor speaks in Dutch, it will be automatically translated into your language for you. And when you speak in your own language, the computer will translate it into Dutch for the doctor. So you can simply speak in your own language.',
    'de-DE': 'Willkommen. Der Computer wird uns bei der Übersetzung während dieses Gesprächs helfen. Wenn der Arzt etwas auf Niederländisch sagt, wird es automatisch in Ihre Sprache übersetzt. Und wenn Sie in Ihrer eigenen Sprache sprechen, übersetzt der Computer es ins Niederländische für den Arzt. Sie können also einfach in Ihrer eigenen Sprache sprechen.',
    'fr-FR': 'Bienvenue. L\'ordinateur va nous aider à traduire pendant cette conversation. Quand le médecin parle en néerlandais, cela sera automatiquement traduit dans votre langue. Et quand vous parlez dans votre propre langue, l\'ordinateur le traduira en néerlandais pour le médecin. Vous pouvez donc simplement parler dans votre propre langue.',
    'fr-BE': 'Bienvenue. L\'ordinateur va nous aider à traduire pendant cette conversation. Quand le médecin parle en néerlandais, cela sera automatiquement traduit dans votre langue. Et quand vous parlez dans votre propre langue, l\'ordinateur le traduira en néerlandais pour le médecin. Vous pouvez donc simplement parler dans votre propre langue.',
    'es-ES': 'Bienvenido. La computadora nos ayudará con la traducción durante esta conversación. Cuando el médico hable en neerlandés, se traducirá automáticamente a su idioma. Y cuando usted hable en su propio idioma, la computadora lo traducirá al neerlandés para el médico. Así que puede hablar tranquilamente en su propio idioma.',
    'pt-PT': 'Bem-vindo. O computador vai ajudar-nos com a tradução durante esta conversa. Quando o médico falar em neerlandês, será automaticamente traduzido para a sua língua. E quando falar na sua própria língua, o computador traduzirá para o neerlandês para o médico. Pode portanto falar na sua própria língua.',
    'pt-BR': 'Bem-vindo. O computador vai nos ajudar com a tradução durante esta conversa. Quando o médico falar em holandês, será automaticamente traduzido para o seu idioma. E quando você falar no seu próprio idioma, o computador traduzirá para o holandês para o médico. Então você pode simplesmente falar no seu próprio idioma.',
    'it-IT': 'Benvenuto. Il computer ci aiuterà con la traduzione durante questa conversazione. Quando il medico parla in olandese, verrà automaticamente tradotto nella sua lingua. E quando lei parla nella sua lingua, il computer lo tradurrà in olandese per il medico. Quindi può semplicemente parlare nella sua lingua.',
    'pl-PL': 'Witamy. Komputer pomoże nam w tłumaczeniu podczas tej rozmowy. Kiedy lekarz mówi po niderlandzku, zostanie to automatycznie przetłumaczone na Pana/Pani język. A kiedy Pan/Pani mówi w swoim języku, komputer przetłumaczy to na niderlandzki dla lekarza. Może więc Pan/Pani po prostu mówić w swoim języku.',
    'tr-TR': 'Hoş geldiniz. Bu görüşme sırasında bilgisayar çeviri konusunda bize yardımcı olacak. Doktor Hollandaca konuştuğunda, sizin dilinize otomatik olarak çevrilecektir. Ve siz kendi dilinizde konuştuğunuzda, bilgisayar bunu doktor için Hollandacaya çevirecektir. Yani kendi dilinizde rahatça konuşabilirsiniz.',
    'ar-SA': 'مرحباً. سيساعدنا الكمبيوتر في الترجمة خلال هذه المحادثة. عندما يتحدث الطبيب بالهولندية، ستتم ترجمتها تلقائياً إلى لغتك. وعندما تتحدث بلغتك الخاصة، سيترجم الكمبيوتر كلامك إلى الهولندية للطبيب. لذا يمكنك التحدث بلغتك الخاصة بكل راحة.',
    'ar-MA': 'مرحباً. سيساعدنا الكمبيوتر في الترجمة خلال هذه المحادثة. عندما يتحدث الطبيب بالهولندية، ستتم ترجمتها تلقائياً إلى لغتك. وعندما تتحدث بلغتك الخاصة، سيترجم الكمبيوتر كلامك إلى الهولندية للطبيب. لذا يمكنك التحدث بلغتك الخاصة بكل راحة.',
    'zh-CN': '欢迎。在这次谈话中，电脑将协助我们进行翻译。当医生用荷兰语说话时，电脑会自动将其翻译成您的语言。当您用自己的语言说话时，电脑会将其翻译成荷兰语给医生。所以您可以放心地用自己的语言说话。',
    'ja-JP': 'ようこそ。この会話中、コンピューターが翻訳を支援します。医師がオランダ語で話すと、自動的にあなたの言語に翻訳されます。そして、あなたが自分の言語で話すと、コンピューターがそれを医師のためにオランダ語に翻訳します。ですから、安心してご自分の言語でお話しください。',
    'ko-KR': '환영합니다. 이 대화 중에 컴퓨터가 번역을 도와줄 것입니다. 의사가 네덜란드어로 말하면 자동으로 귀하의 언어로 번역됩니다. 그리고 귀하가 자신의 언어로 말하면 컴퓨터가 의사를 위해 네덜란드어로 번역합니다. 그러니 편하게 자신의 언어로 말씀하세요.',
    'ru-RU': 'Добро пожаловать. Компьютер поможет нам с переводом во время этого разговора. Когда врач говорит по-нидерландски, это будет автоматически переведено на ваш язык. А когда вы говорите на своём языке, компьютер переведёт это на нидерландский для врача. Так что вы можете спокойно говорить на своём языке.',
    'uk-UA': 'Ласкаво просимо. Комп\'ютер допоможе нам з перекладом під час цієї розмови. Коли лікар говорить нідерландською, це буде автоматично перекладено на вашу мову. А коли ви говорите своєю мовою, комп\'ютер перекладе це на нідерландську для лікаря. Тож ви можете спокійно говорити своєю мовою.',
    'ro-RO': 'Bine ați venit. Computerul ne va ajuta cu traducerea în timpul acestei conversații. Când medicul vorbește în olandeză, va fi tradus automat în limba dumneavoastră. Și când vorbiți în limba dumneavoastră, computerul va traduce în olandeză pentru medic. Așadar, puteți vorbi liniștit în limba dumneavoastră.',
    'hi-IN': 'स्वागत है। इस बातचीत के दौरान कंप्यूटर अनुवाद में हमारी मदद करेगा। जब डॉक्टर डच में बोलेंगे, तो यह स्वचालित रूप से आपकी भाषा में अनुवाद हो जाएगा। और जब आप अपनी भाषा में बोलेंगे, तो कंप्यूटर इसे डॉक्टर के लिए डच में अनुवाद करेगा। तो आप बेझिझक अपनी भाषा में बोल सकते हैं।',
    'fa-IR': 'خوش آمدید. کامپیوتر در طول این مکالمه به ما در ترجمه کمک خواهد کرد. وقتی دکتر به هلندی صحبت می‌کند، به طور خودکار به زبان شما ترجمه می‌شود. و وقتی شما به زبان خودتان صحبت می‌کنید، کامپیوتر آن را برای دکتر به هلندی ترجمه می‌کند. پس می‌توانید با خیال راحت به زبان خودتان صحبت کنید.',
    'ur-PK': 'خوش آمدید۔ اس گفتگو کے دوران کمپیوٹر ترجمے میں ہماری مدد کرے گا۔ جب ڈاکٹر ڈچ میں بات کریں گے تو یہ خود بخود آپ کی زبان میں ترجمہ ہو جائے گا۔ اور جب آپ اپنی زبان میں بات کریں گے تو کمپیوٹر ڈاکٹر کے لیے اسے ڈچ میں ترجمہ کرے گا۔ تو آپ بے فکر ہو کر اپنی زبان میں بات کر سکتے ہیں۔',
  };
  const DEFAULT_INTRO = 'Welcome. The computer will help us with translation during this conversation. When the doctor speaks in Dutch, it will be automatically translated into your language. And when you speak in your own language, the computer will translate it into Dutch for the doctor. So you can simply speak in your own language.';

  // Explanation that the consultation is recorded for medical documentation
  const RECORDING_TEXTS: Record<string, string> = {
    'nl-NL': 'Uw dokter gebruikt een app die alles wat wij bespreken opneemt. Dit gebeurt alleen om het medisch verslag van dit consult te kunnen maken.',
    'en-US': 'Your doctor is using an app that records everything we discuss. This is done only to create the medical report of this consultation.',
    'en-GB': 'Your doctor is using an app that records everything we discuss. This is done only to create the medical report of this consultation.',
    'de-DE': 'Ihr Arzt verwendet eine App, die alles aufzeichnet, was wir besprechen. Dies dient ausschließlich der medizinischen Dokumentation dieses Gesprächs.',
    'fr-FR': "Votre médecin utilise une application qui enregistre tout ce que nous disons. Cela sert uniquement à rédiger le compte rendu médical de cette consultation.",
    'fr-BE': "Votre médecin utilise une application qui enregistre tout ce que nous disons. Cela sert uniquement à rédiger le compte rendu médical de cette consultation.",
    'es-ES': 'Su médico utiliza una aplicación que graba todo lo que hablamos. Esto se hace únicamente para elaborar el informe médico de esta consulta.',
    'pt-PT': 'O seu médico utiliza uma aplicação que grava tudo o que falamos. Isto serve apenas para elaborar o relatório médico desta consulta.',
    'pt-BR': 'Seu médico usa um aplicativo que grava tudo o que conversamos. Isso é feito apenas para elaborar o relatório médico desta consulta.',
    'it-IT': 'Il suo medico utilizza un\'applicazione che registra tutto ciò di cui parliamo. Questo serve unicamente a redigere il referto medico di questa visita.',
    'pl-PL': 'Pana/Pani lekarz korzysta z aplikacji, która nagrywa wszystko, o czym rozmawiamy. Służy to wyłącznie sporządzeniu dokumentacji medycznej tej wizyty.',
    'tr-TR': 'Doktorunuz, konuştuğumuz her şeyi kaydeden bir uygulama kullanıyor. Bu yalnızca bu muayenenin tıbbi raporunu hazırlamak için yapılmaktadır.',
    'ar-SA': 'يستخدم طبيبك تطبيقاً يسجل كل ما نتحدث عنه. يتم ذلك فقط لإعداد التقرير الطبي لهذه الزيارة.',
    'ar-MA': 'يستخدم طبيبك تطبيقاً يسجل كل ما نتحدث عنه. يتم ذلك فقط لإعداد التقرير الطبي لهذه الزيارة.',
    'zh-CN': '您的医生正在使用一个应用程序，它会记录我们谈话的全部内容。这仅用于撰写本次就诊的医疗记录。',
    'ja-JP': '医師は、私たちの会話をすべて録音するアプリを使用しています。これは、この診察の医療記録を作成するためだけに行われます。',
    'ko-KR': '의사는 우리가 나누는 모든 대화를 녹음하는 앱을 사용하고 있습니다. 이는 이번 진료의 의료 기록을 작성하기 위한 목적으로만 사용됩니다.',
    'ru-RU': 'Ваш врач использует приложение, которое записывает всё, о чём мы говорим. Это делается только для составления медицинского заключения по этому приёму.',
    'uk-UA': 'Ваш лікар користується застосунком, який записує все, про що ми говоримо. Це робиться лише для складання медичного запису цієї консультації.',
    'ro-RO': 'Medicul dumneavoastră folosește o aplicație care înregistrează tot ce discutăm. Acest lucru se face doar pentru a întocmi raportul medical al acestei consultații.',
    'hi-IN': 'आपके डॉक्टर एक ऐप का उपयोग कर रहे हैं जो हमारी सारी बातचीत रिकॉर्ड करता है। यह केवल इस परामर्श की चिकित्सा रिपोर्ट बनाने के लिए किया जाता है।',
    'fa-IR': 'پزشک شما از برنامه‌ای استفاده می‌کند که تمام گفتگوی ما را ضبط می‌کند. این کار فقط برای تهیه گزارش پزشکی این ویزیت انجام می‌شود.',
    'ur-PK': 'آپ کے ڈاکٹر ایک ایپ استعمال کر رہے ہیں جو ہماری تمام گفتگو ریکارڈ کرتی ہے۔ یہ صرف اس معائنے کی طبی رپورٹ تیار کرنے کے لیے کیا جاتا ہے۔',
  };
  const DEFAULT_RECORDING = 'Your doctor is using an app that records everything we discuss. This is done only to create the medical report of this consultation.';

  const [isPlayingIntro, setIsPlayingIntro] = useState(false);
  // Ter plekke vertaalde introteksten, zodat nog eens afspelen niets extra's kost.
  const introVertalingenRef = useRef<Map<string, string>>(new Map());

  const vertaalIntroTekst = useCallback(async (bron: string, doelTaal: string, terugval?: string): Promise<string> => {
    if (doelTaal.split('-')[0] === 'nl') return bron;
    const sleutel = `${doelTaal}|${bron}`;
    const gecacht = introVertalingenRef.current.get(sleutel);
    if (gecacht) return gecacht;
    try {
      const { vertaling, kosten } = await vertaal(bron, getLanguageLabel(doelTaal), 'soep');
      if (!vertaling) return terugval ?? bron;
      voegKostenToe('translation', kosten);
      introVertalingenRef.current.set(sleutel, vertaling);
      return vertaling;
    } catch (e) {
      // Liever een tekst die de patiënt half verstaat dan helemaal geen uitleg.
      console.error('Vertalen van de introductie mislukt:', e);
      return terugval ?? bron;
    }
  }, [voegKostenToe]);

  const handlePlayIntro = useCallback(async () => {
    if (isPlayingIntro) return;
    setIsPlayingIntro(true);
    try {
      const recordingText = RECORDING_TEXTS[patientLang]
        || await vertaalIntroTekst(RECORDING_TEXTS['nl-NL'], patientLang, DEFAULT_RECORDING);

      // Spreken beiden dezelfde taal, dan valt er niets te vertalen en slaan we die uitleg over.
      let translationText = '';
      if (patientLang.split('-')[0] !== providerLang.split('-')[0]) {
        translationText = providerLang.startsWith('nl')
          ? (INTRO_TEXTS[patientLang] || await vertaalIntroTekst(INTRO_TEXTS['nl-NL'], patientLang, DEFAULT_INTRO))
          : await vertaalIntroTekst(bouwVertaalUitleg(providerLang), patientLang);
      }

      const introText = [recordingText, translationText].filter(Boolean).join(' ');
      const voice = getVoiceForLanguage(patientLang);
      const { audio: audioBlob, kosten: introKosten } = await tekstNaarSpraak(introText, voice);
      voegKostenToe('recording', introKosten);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      await new Promise<void>((resolve) => {
        audio.onended = () => { URL.revokeObjectURL(audioUrl); resolve(); };
        audio.onerror = () => { URL.revokeObjectURL(audioUrl); resolve(); };
        audio.play().catch(() => resolve());
      });
    } catch (error) {
      console.error('Intro TTS failed:', error);
      toast({ title: 'Introductie afspelen mislukt', variant: 'destructive' });
    } finally {
      setIsPlayingIntro(false);
      audioRef.current = null;
    }
  }, [patientLang, providerLang, isPlayingIntro, vertaalIntroTekst]);

  const rollbackQueuedReadAloudIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    ids.forEach(id => readAloudIdsRef.current.delete(id));
    setReadAloudIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const handlePlayTTS = async (msg: TranscriptMessage) => {
    // Manual click: if already playing this message, stop
    if (playingMessageId === msg.id && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingMessageId(null);
      ttsQueueRef.current = [];
      isPlayingQueueRef.current = false;
      return;
    }
    // Stop current, clear queue, play this one
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    ttsQueueRef.current = [];
    isPlayingQueueRef.current = false;
    setPlayingMessageId(msg.id);
    const role = speakerRolesRef.current[msg.speaker];
    const isDoctor = role === 'arts';
    console.log('[TTS DEBUG] handlePlayTTS', { speaker: msg.speaker, role, isDoctor, speakerRoles: { ...speakerRolesRef.current } });
    await playTTSForMessage(msg, { forceProviderLang: !isDoctor });
    readAloudIdsRef.current.add(msg.id);
    setReadAloudIds(prev => new Set(prev).add(msg.id));
  };

  // Keep lastAutoPlayedCount in sync so trigger-phrase logic doesn't replay old messages
  useEffect(() => {
    lastAutoPlayedCount.current = messages.length;
  }, [messages.length]);

  // Keep triggerCommandIdsRef in sync
  useEffect(() => { triggerCommandIdsRef.current = triggerCommandIds; }, [triggerCommandIds]);

  // Shared function: translate unread messages and queue for TTS
  const triggerTranslateAndRead = useCallback((triggerMsgId?: string) => {
    if (isTranslateQueueRunningRef.current) return;
    lastTranslatedQueueRef.current = [];
    const currentMessages = messagesRef.current;
    if (currentMessages.length === 0) return;

    // Collect unread messages excluding trigger commands
    const queuedIds = new Set(ttsQueueRef.current.map(m => m.id));
    const unread = currentMessages.filter(
      m => !m.isPartial && !readAloudIdsRef.current.has(m.id) && !triggerCommandIdsRef.current.has(m.id) && m.id !== triggerMsgId && !queuedIds.has(m.id)
    );
    if (unread.length === 0) return;

    // Mark all unread as "will be read" immediately to prevent double-queueing
    for (const m of unread) {
      readAloudIdsRef.current.add(m.id);
    }
    setReadAloudIds(prev => {
      const next = new Set(prev);
      for (const m of unread) next.add(m.id);
      return next;
    });

    const translateAndQueue = async () => {
      isTranslateQueueRunningRef.current = true;
      const queuedMessageIds = new Set<string>();
      try {
        const langsDiffer = providerLang.split('-')[0] !== patientLang.split('-')[0];

        console.log('[TRIGGER] Processing', unread.length, 'unread messages, langsDiffer:', langsDiffer);
        for (const m of unread) {
          let translatedText = m.text;
          if (langsDiffer) {
            const role = speakerRolesRef.current[m.speaker];
            const isDoctor = role === 'arts';
            const targetLangCode = isDoctor ? patientLang : providerLang;
            const targetLangLabel = getLanguageLabel(targetLangCode);
            try {
              console.log('[TRANSLATE] Translating message', m.id, 'from', isDoctor ? 'doctor' : 'patient', 'to', targetLangLabel, ':', m.text.substring(0, 60));
              const { vertaling, kosten } = await vertaal(m.text, targetLangLabel, 'soep');
              if (vertaling) {
                translatedText = vertaling;
                updateMessage(m.id, { translatedText: vertaling });
                voegKostenToe('translation', kosten);
              }
            } catch (e) {
              const idsToRollback = unread
                .map(message => message.id)
                .filter(id => !queuedMessageIds.has(id));
              rollbackQueuedReadAloudIds(idsToRollback);
              lastProcessedTriggerIdRef.current = null;
              console.error('Trigger translation failed for message:', m.id, e);
              toast({
                title: 'Vertaling mislukt',
                description: e instanceof Error ? e.message : 'Azure kon de tekst niet vertalen.',
                variant: 'destructive',
              });
              return;
            }
          }
          const queuedMsg = { ...m, translatedText };
          ttsQueueRef.current.push(queuedMsg);
          lastTranslatedQueueRef.current.push(queuedMsg);
          queuedMessageIds.add(m.id);
          void processQueue();
        }
      } finally {
        isTranslateQueueRunningRef.current = false;
      }
    };

    translateAndQueue().catch(err => console.error('Trigger translate+queue failed:', err));
  }, [processQueue, providerLang, patientLang, voegKostenToe, updateMessage, rollbackQueuedReadAloudIds]);

  // Trigger phrase: when someone says the trigger phrase, translate unread messages then queue for TTS
  // Always treat trigger phrase as coming from the doctor (Azure diarization can misattribute speakers)
  useEffect(() => {
    if (!VOICE_TRANSLATE_COMMAND_ENABLED) return;
    const triggerPhrase = settings.read_trigger_phrase?.trim().toLowerCase();
    if (!triggerPhrase) return;
    if (messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg.isPartial) return;

    const text = (lastMsg.translatedText || lastMsg.text);
    if (!isOnlyPhrase(text, triggerPhrase)) return;

    // Guard: don't re-process same trigger message
    if (lastProcessedTriggerIdRef.current === lastMsg.id) return;
    lastProcessedTriggerIdRef.current = lastMsg.id;

    // Auto-assign this speaker as doctor if not already
    if (speakerRolesRef.current[lastMsg.speaker] !== 'arts') {
      assignDoctorRole(lastMsg.speaker);
      setDoctorAutoAssigned(true);
    }

    // Mark this message as a trigger command
    triggerCommandIdsRef.current = new Set(triggerCommandIdsRef.current).add(lastMsg.id);
    setTriggerCommandIds(prev => new Set(prev).add(lastMsg.id));
    readAloudIdsRef.current.add(lastMsg.id);
    setReadAloudIds(prev => new Set(prev).add(lastMsg.id));

    triggerTranslateAndRead(lastMsg.id);
  }, [messages, triggerTranslateAndRead, assignDoctorRole, settings]);

  // Repeat last translation: replay the last batch of translated messages via TTS
  const repeatLastTranslation = useCallback(() => {
    const lastBatch = lastTranslatedQueueRef.current;
    if (lastBatch.length === 0) {
      console.log('[TRIGGER] No previous translation to repeat');
      return;
    }
    console.log('[TRIGGER] Repeating last translation —', lastBatch.length, 'messages');
    for (const msg of lastBatch) {
      ttsQueueRef.current.push(msg);
    }
    void processQueue();
  }, [processQueue]);

  // Keyboard shortcuts for remote presenter and keyboard control
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        console.log(`[TRIGGER] ${e.key} pressed — triggering translation`);
        triggerTranslateAndRead();
      } else if (e.key === 'PageUp') {
        e.preventDefault();
        console.log('[TRIGGER] PageUp pressed — repeating last translation');
        repeatLastTranslation();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (isRecording && !isPaused) {
          pauseRecording();
        } else if (isRecording && isPaused) {
          resumeRecording();
        }
      } else if (e.key === 'Escape' || e.key === 'F5') {
        e.preventDefault();
        if (isRecording && !isPaused) {
          console.log(`[TRIGGER] ${e.key} pressed — pausing recording`);
          pauseRecording();
        }
      } else if (e.key === '.') {
        e.preventDefault();
        if (isRecording && isPaused) {
          console.log('[TRIGGER] . pressed — resuming recording');
          resumeRecording();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerTranslateAndRead, repeatLastTranslation, isRecording, isPaused, pauseRecording, resumeRecording]);



  const handleStopAndProcess = async () => {
    stopRecording();

    // Log recording duration
    if (recordingStartTime.current) {
      const durationSeconds = Math.round((Date.now() - recordingStartTime.current) / 1000);
      recordingStartTime.current = null;
      if (durationSeconds > 0) {
        void meldOpnameduur(durationSeconds);
        // De kosten komen mee in het antwoord van /api/usage; meldOpnameduur werkt
        // de teller bij.
      }
    }
    
    if (!fullTranscript.trim()) {
      toast({
        title: "Geen tekst",
        description: "Er is geen spraak opgenomen om te verwerken.",
        variant: "destructive",
      });
      return;
    }

    // Build transcript with role labels
    const labeledTranscript = messages
      .filter(m => !triggerCommandIds.has(m.id))
      .map(m => {
        const role = getSpeakerDisplayName(m.speaker);
        const text = m.translatedText || m.text;
        return `${role}: ${text}`;
      }).join('\n').trim();

    setIsProcessing(true);
    const transcriptText = labeledTranscript || fullTranscript.trim();

    try {
      /**
       * Eén aanroep, en geen controle op endpoint of sleutel meer -- die staan niet
       * langer in de browser. De server bouwt de prompt uit het profiel van de
       * ingelogde gebruiker, kiest het model, en logt het verbruik met de kosten die
       * hij teruggeeft.
       */
      const data = await transcriptNaarSoep(transcriptText, multipleProblems);

      if (!data?.soep) {
        throw new Error('Geen SOEP-data ontvangen');
      }

      voegKostenToe('summarization', data.cost);
      onSoepGenerated(data.soep, transcriptText, data.problems, data.generationId);
      onClose();
    } catch (error) {
      console.error('Transcript verwerken mislukt:', error);
      toast({
        title: 'Verwerking mislukt',
        // De API geeft een begrijpelijke melding met een code; die is beter dan een
        // eigen tekst, want hij zegt wát er mis is -- geen sessie, geen
        // AI-configuratie, of een storing bij Azure.
        description: metHerhaalAdvies(
          error instanceof Error && error.message
            ? error.message
            : 'Kon het transcript niet omzetten naar SOEP-formaat. Probeer opnieuw.',
          error,
        ),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (isRecording) {
      stopRecording();
      // Log recording duration on cancel too
      if (recordingStartTime.current) {
        const durationSeconds = Math.round((Date.now() - recordingStartTime.current) / 1000);
        recordingStartTime.current = null;
        if (durationSeconds > 0) {
          void meldOpnameduur(durationSeconds);
        }
      }
    }
    onClose();
  };

  const allMessages = [...messages, ...(partialMessage ? [partialMessage] : [])];

  // Determine TTS language label for display
  const patientLangLabel = getLanguageLabel(patientLang);
  const providerLangLabel = getLanguageLabel(providerLang);

  const uniqueSpeakers = [...new Set(messages.map(m => m.speaker).filter(Boolean))];
  const showRoleHint = uniqueSpeakers.length >= 2 && !hasAnyRoleAssigned;

  return (
    <div className="h-full flex flex-col">
      <VerlatenWaarschuwing
        actief={heeftOnbewaardConsult}
        titel={isRecording ? 'De opname loopt nog' : 'Het transcript is nog niet verwerkt'}
        beschrijving={
          isRecording
            ? 'Als u dit scherm verlaat stopt de opname en gaat het transcript verloren. Er is nog geen SOEP-verslag gemaakt.'
            : 'Als u dit scherm verlaat gaat het transcript van dit consult verloren. Er is nog geen SOEP-verslag gemaakt.'
        }
      />
      <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-primary/10 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {isConnecting ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span className="text-xs font-medium">Verbinden...</span>
            </div>
          ) : isRecording && !isPaused ? (
            <div className="flex items-center gap-2">
              <span className="relative flex h-3.5 w-3.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-destructive"></span>
              </span>
              <span className="text-xs font-medium text-destructive">
                Opname bezig...
                {isTranslating ? (
                  <span className="text-muted-foreground"> — vertalen naar NL…</span>
                ) : null}
              </span>
            </div>
          ) : isRecording && isPaused ? (
            <div className="flex items-center gap-2">
              <Pause className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Opname gepauzeerd</span>
            </div>
          ) : isProcessing ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span className="text-xs font-medium">AI verwerkt naar SOEP...</span>
            </div>
          ) : settings.calling_enabled ? (
            <div className="flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input
                type="tel"
                inputMode="tel"
                placeholder="0612345678 of extensie"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={sip.isInCall}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCallToggle(); }}
                className="h-7 w-40 text-[11px] px-2"
              />
              <Button
                variant={sip.isInCall ? 'destructive' : 'default'}
                size="sm"
                className="h-7 px-2 text-[11px] gap-1"
                onClick={handleCallToggle}
                disabled={sip.state === 'registering'}
                title={sip.isInCall ? 'Gesprek beëindigen' : (sipConfig ? (sip.state === 'idle' || sip.state === 'register-failed' || sip.state === 'ended' ? 'SIP verbinden' : 'Bellen') : 'SIP niet geconfigureerd')}
              >
                {sip.state === 'registering' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : sip.isInCall ? (
                  <PhoneOff className="h-3 w-3" />
                ) : (
                  <Phone className="h-3 w-3" />
                )}
                <span>
                  {sip.state === 'registering' ? 'Verbinden…'
                    : sip.state === 'register-failed' ? 'Offline'
                    : sip.state === 'ringing-out' ? 'Belt over…'
                    : sip.state === 'ringing-in' ? 'Inkomend'
                    : sip.state === 'in-call' && sip.call
                      ? `In gesprek ${formatDuration(Date.now() - sip.call.startedAt)}`
                    : sip.state === 'registered' ? 'Bel' : 'Verbind'}
                </span>
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          {/* Taal van de zorgverlener */}
          <Select value={providerLang} onValueChange={setProviderLang}>
            <SelectTrigger
              className="h-7 w-auto min-w-0 gap-1 border-none bg-transparent px-1.5 text-[11px] focus:ring-0 focus:ring-offset-0"
              title="Taal van de zorgverlener"
            >
              <Stethoscope className="h-3 w-3 text-primary shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AZURE_SPEECH_LANGUAGES.map(lang => (
                <SelectItem key={lang.code} value={lang.code} className="text-xs">
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Patient language dropdown */}
          <Select value={patientLang} onValueChange={setPatientLang}>
            <SelectTrigger className="h-7 w-auto min-w-0 gap-1 border-none bg-transparent px-1.5 text-[11px] focus:ring-0 focus:ring-offset-0">
              <User className="h-3 w-3 text-muted-foreground shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AZURE_SPEECH_LANGUAGES.map(lang => (
                <SelectItem key={lang.code} value={lang.code} className="text-xs">
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Uitleg opname + vertaling afspelen in de taal van de patiënt */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handlePlayIntro}
            disabled={isPlayingIntro}
            title="Uitleg opname en vertaling afspelen in de taal van de patiënt"
          >
            {isPlayingIntro ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            ) : (
              <MessageCircle className="h-3.5 w-3.5 text-primary" />
            )}
          </Button>

          {/* Eén SOEP-verslag afdwingen (per sessie) */}
          <div
            className="flex items-center gap-1 h-7 px-1.5"
            title="Alle besproken onderwerpen samenvatten in één SOEP-verslag"
          >
            <Label htmlFor="single-soep-toggle" className="text-[11px] text-muted-foreground cursor-pointer whitespace-nowrap">
              1 SOEP
            </Label>
            <Switch
              id="single-soep-toggle"
              checked={singleSoep}
              onCheckedChange={setSingleSoep}
              className="scale-75"
            />
          </div>

          {onOpenManual && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onOpenManual} title="Handleiding">
              <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          )}

          {settings.live_transcript_enabled && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleOpenLiveTranscript}
              title="Live transcriptie delen (QR)"
            >
              <QrCode className="h-3.5 w-3.5 text-primary" />
            </Button>
          )}

          {onOpenSettings && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onOpenSettings} title="Instellingen">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>

      {/* Role assignment hint */}
      {(showRoleHint || hasAnyRoleAssigned) && (
        <div className="px-3 py-1.5 bg-accent/30 border-b text-[10px] text-muted-foreground flex items-center gap-1.5 shrink-0">
          <Stethoscope className="h-3 w-3 text-primary shrink-0" />
          <span className="flex-1 min-w-0 truncate">
            {hasAnyRoleAssigned
              ? 'Rollen automatisch bepaald — corrigeer per bericht met Dokter/Pati\u00ebnt.'
              : 'Markeer per bericht wie de Zorgverlener en wie de Pati\u00ebnt is.'}
          </span>
          {hasAnyRoleAssigned && (
            <Button
              variant="outline"
              size="sm"
              className="h-5 px-1.5 text-[10px] gap-1 shrink-0"
              onClick={swapRoles}
            >
              <ArrowLeftRight className="h-2.5 w-2.5" />
              Rollen omdraaien
            </Button>
          )}
        </div>
      )}

      {/* Cost display - always visible at top */}
      {costs.total > 0 && (
        <div className="px-3 py-1 border-b bg-muted/30 flex items-center gap-2 text-[10px] text-muted-foreground shrink-0">
          <Coins className="h-3 w-3 shrink-0" />
          <span className="font-medium">Consult kosten:</span>
          {costs.recording > 0 && <span>Opname €{costs.recording.toFixed(2)}</span>}
          {costs.translation > 0 && <span>· Vertaling €{costs.translation.toFixed(2)}</span>}
          {costs.summarization > 0 && <span>· SOEP €{costs.summarization.toFixed(2)}</span>}
          {costs.icpc > 0 && <span>· ICPC €{costs.icpc.toFixed(2)}</span>}
          <span className="font-semibold text-foreground ml-auto">Totaal €{costs.total.toFixed(2)}</span>
        </div>
      )}

      {/* WhatsApp-style chat messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5 min-h-0 bg-muted/10">
        {allMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            {!isRecording && !isConnecting && !isProcessing ? (
              <Button
                size="lg"
                className="h-20 w-20 rounded-full p-0 shadow-lg"
                onClick={handleStartRecording}
              >
                <Mic className="h-8 w-8" />
              </Button>
            ) : null}
            <p className="text-xs text-muted-foreground italic">
              {isRecording ? 'Spreek nu... De berichten verschijnen hier.' : isConnecting ? 'Verbinden...' : 'Klik op de microfoon om te beginnen met opnemen.'}
            </p>
          </div>
        ) : (
          allMessages.map((msg) => {
            const role = getSpeakerRole(msg.speaker);
            const isDoctor = role === 'arts';
            const isPatient = role === 'patient';
            const isPlaying = playingMessageId === msg.id;
            const displayText = msg.translatedText || msg.text;
            
            // WhatsApp-style: doctor on right, patient on left
            const alignRight = isDoctor;
            const bgColor = isDoctor ? DOCTOR_BG : isPatient ? PATIENT_BG : UNKNOWN_BG;
            
            if (triggerCommandIds.has(msg.id)) {
              return (
                <div key={msg.id} className="flex items-center gap-2 py-0.5">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-[9px] text-muted-foreground/60 italic shrink-0">Vertalen</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={`flex ${alignRight ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex flex-col max-w-[85%] rounded-md px-2 py-1 shadow-sm ${bgColor} ${
                    msg.isPartial ? 'opacity-60' : ''
                  } border border-border/40 ${
                    alignRight ? 'rounded-tr-sm' : 'rounded-tl-sm'
                  }`}
                >
                  {/* Speaker name - clickable for role assignment */}
                  <div className="flex items-center gap-1">
                    {isDoctor ? (
                      <Stethoscope className="h-2.5 w-2.5 text-primary shrink-0" />
                    ) : isPatient ? (
                      <User className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/50" />
                    )}
                    <span className="text-[10px] font-semibold text-foreground/70">
                      {getSpeakerDisplayName(msg.speaker || 'Spreker')}
                    </span>
                    {msg.speaker && (
                      <span className="flex items-center gap-0.5 ml-1">
                        <button
                          type="button"
                          onClick={() => assignSpeakerRole(msg.speaker, 'arts')}
                          title="Markeer als Zorgverlener"
                          className={`px-1 py-[1px] rounded text-[9px] border ${isDoctor ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                        >
                          Dokter
                        </button>
                        <button
                          type="button"
                          onClick={() => assignSpeakerRole(msg.speaker, 'patient')}
                          title="Markeer als Patiënt"
                          className={`px-1 py-[1px] rounded text-[9px] border ${isPatient ? 'bg-secondary text-secondary-foreground border-border' : 'border-border text-muted-foreground hover:text-foreground'}`}
                        >
                          Patiënt
                        </button>
                      </span>
                    )}

                    <span className="text-[9px] text-muted-foreground ml-auto">
                      {msg.timestamp.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  </div>
                  
                  {/* Message text */}
                  <p className="text-[11px] text-foreground leading-snug">
                    {displayText || (
                      <span className="italic text-muted-foreground">{msg.text}</span>
                    )}
                    {msg.isPartial && (
                      <span className="inline-block ml-1">
                        <Loader2 className="h-3 w-3 animate-spin inline text-muted-foreground" />
                      </span>
                    )}
                  </p>

                  {/* TTS play button + read-aloud indicator */}
                  {!msg.isPartial && displayText && (
                    <div className={`flex items-center gap-1 ${alignRight ? 'justify-start' : 'justify-end'} -mt-0.5`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1.5 text-[10px] gap-1 text-muted-foreground hover:text-foreground"
                        onClick={() => handlePlayTTS(msg)}
                        disabled={!!playingMessageId && playingMessageId !== msg.id}
                      >
                        {isPlaying ? (
                          <>
                            <Square className="h-2.5 w-2.5 fill-current" />
                            Stop
                          </>
                        ) : (
                          <>
                            <Volume2 className="h-2.5 w-2.5" />
                            Voorlezen ({role === 'arts' ? patientLangLabel : providerLangLabel})
                          </>
                        )}
                      </Button>
                      {readAloudIds.has(msg.id) && (
                        <span className="flex items-center gap-0.5 text-[9px] text-primary/70" title="Voorgelezen">
                          <Check className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Transcriptie is administratieve ondersteuning, geen medisch oordeel */}
      {allMessages.length > 0 && (
        <div className="px-3 py-1.5 border-t bg-muted/20 text-[10px] leading-snug text-muted-foreground flex items-start gap-1.5 shrink-0">
          <Info className="h-3 w-3 mt-[1px] shrink-0" />
          <span>
            Deze transcriptie is een automatisch gegenereerde tekstweergave ter administratieve
            ondersteuning van de zorgverlener en bevat geen medische analyse, interpretatie of advies.
            De zorgverlener blijft verantwoordelijk voor de juistheid, volledigheid en het gebruik van
            de inhoud in het dossier.
          </span>
        </div>
      )}

      {/* Action buttons + cost display */}
      <div className="px-3 py-2 border-t bg-background shrink-0 space-y-1">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {isRecording && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={isPaused ? resumeRecording : pauseRecording}
              >
                {isPaused ? (
                  <>
                    <Play className="h-3 w-3" />
                    Hervat
                  </>
                ) : (
                  <>
                    <Pause className="h-3 w-3" />
                    Pauzeer
                  </>
                )}
              </Button>
            )}
            {isRecording && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={() => triggerTranslateAndRead()}
                disabled={providerLang === patientLang}
              >
                <Globe className="h-3 w-3" />
                Vertaal
              </Button>
            )}
            {isRecording && (
              <Button
                size="sm"
                variant="destructive"
                className="h-7 text-xs gap-1.5"
                onClick={handleStopAndProcess}
                disabled={isProcessing || isConnecting}
              >
                <MicOff className="h-3 w-3" />
                Stop & genereer SOEP
              </Button>
            )}
          </div>

          {fullTranscript && !isRecording && !isProcessing && (
            <Button
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={handleStopAndProcess}
            >
              <Sparkles className="h-3 w-3" />
              Genereer SOEP
            </Button>
        )}
        </div>
      </div>
      </div>

      <Dialog open={liveDialogOpen} onOpenChange={setLiveDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              Live transcriptie delen
            </DialogTitle>
            <DialogDescription>
              Laat de patiënt deze QR-code scannen met de telefoon. De transcriptie verschijnt dan live op het scherm. De code kan slechts één keer worden gescand.
            </DialogDescription>
          </DialogHeader>
          {liveLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : liveError ? (
            <div className="text-sm text-destructive py-4">{liveError}</div>
          ) : liveQrDataUrl && liveUrl ? (
            <div className="flex flex-col items-center gap-3 py-2">
              <img src={liveQrDataUrl} alt="QR-code" className="w-64 h-64 border rounded-md bg-white p-2" />
              <div className="w-full flex items-center gap-1">
                <Input value={liveUrl} readOnly className="text-[11px] font-mono" />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(liveUrl);
                    toast({ title: 'Link gekopieerd' });
                  }}
                  title="Kopieer link"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                De link kan eenmalig worden geopend en verloopt automatisch.
              </p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
